import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef } from "react";

import { prepareMoneyFlowRequestDto } from "@/api/hooks/stat-analytics-hooks";
import { MoneyFlowRequestDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

export const useGetPlayersTimingsDetailed = (
  filter: MoneyFlowRequestDto | null,
  period: number | null,
  key: number | null
) => {
  const { statAnalyticsApi } = useApi();
  return useQuery({
    queryKey: [
      `/statistic-analytic/analytics/players-timings-detailed-statistic`,
      filter,
      period,
      key,
    ],
    queryFn: ({ signal }) => {
      return statAnalyticsApi.getPlayersTimingsDetailed(
        {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          step_ms: period!,
          query: prepareMoneyFlowRequestDto(filter as MoneyFlowRequestDto),
        },
        {
          signal,
        }
      );
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!filter && !!period && !!key,
  });
};

export const useGetTimings = (
  filter: MoneyFlowRequestDto | null,
  period: number | null,
  key: number | null
) => {
  const isInitialized = useRef(false);
  const {
    data: statAnalyticsResponse,
    isLoading,
    error: moneyFlowError,
    status: moneyFlowStatus,
  } = useGetPlayersTimingsDetailed(filter, period, key);

  useEffect(() => {
    if (moneyFlowStatus && moneyFlowStatus === "error") {
      const message = (moneyFlowError as unknown as AxiosError<{ message: string }>)?.response
        ?.data.message;
      enqueueSnackbar(`${message?.slice ? message.slice(0, 500) : message}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moneyFlowStatus]);

  const data = useMemo(() => {
    return statAnalyticsResponse?.data.data;
  }, [statAnalyticsResponse]);

  useEffect(() => {
    if (data) {
      isInitialized.current = !!data;
    }
  }, [data]);

  return {
    isLoading,
    data,
  };
};
